import { useState } from "react";
import { styles } from "../styles";

/**
 * Inline contacts manager (add / edit / soft-delete). Works for either
 * a customer-level contact (locationId === null) or a location-level
 * contact (locationId set). Each action writes through immediately —
 * contacts are first-class records, not staged like the surrounding
 * form's fields.
 *
 * Used from CustomersPage (customer-level) and CustomerDetailPage
 * (location-level).
 */
export default function ContactsEditor({
  customerId,
  locationId = null,
  contacts,
  actions,
  currentUserEmail,
  title = "Contacts (by Department)",
}) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const startAdd = () =>
    setDraft({ name: "", department: "", phone: "", email: "" });
  const startEdit = (c) =>
    setDraft({
      id: c.id,
      name: c.name || "",
      department: c.department || "",
      phone: c.phone || "",
      email: c.email || "",
    });
  const cancel = () => {
    setDraft(null);
    setErr("");
  };

  const save = async () => {
    if (!draft.name.trim()) {
      setErr("Contact name is required.");
      return;
    }
    try {
      setBusy(true);
      setErr("");
      if (draft.id) {
        await actions.updateContact(draft.id, {
          name: draft.name.trim(),
          department: draft.department.trim(),
          phone: draft.phone.trim(),
          email: draft.email.trim(),
        });
      } else {
        const id = `c_${Date.now()}`;
        await actions.addContact(customerId, locationId, {
          id,
          name: draft.name.trim(),
          department: draft.department.trim(),
          phone: draft.phone.trim(),
          email: draft.email.trim(),
        });
      }
      cancel();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete contact "${c.name || "(unnamed)"}"?`)) return;
    try {
      await actions.softDeleteContact(c.id, currentUserEmail);
    } catch (e) {
      setErr(e.message);
    }
  };

  const byDept = new Map();
  for (const c of contacts) {
    const key = (c.department || "Other").trim() || "Other";
    if (!byDept.has(key)) byDept.set(key, []);
    byDept.get(key).push(c);
  }
  const deptOrder = [...byDept.keys()].sort();

  return (
    <div>
      <h4 style={{ margin: "16px 0 6px" }}>{title}</h4>
      {err && (
        <div style={{ color: "red", fontSize: "12px", marginBottom: "6px" }}>
          {err}
        </div>
      )}

      {contacts.length === 0 && !draft && (
        <div
          style={{
            fontSize: "13px",
            color: "#666",
            fontStyle: "italic",
            marginBottom: "8px",
          }}
        >
          No contacts yet.
        </div>
      )}

      {deptOrder.map((dept) => (
        <div key={dept} style={{ marginBottom: "8px" }}>
          <div
            style={{
              fontSize: "12px",
              color: "#666",
              fontWeight: 600,
              marginBottom: "2px",
            }}
          >
            {dept}
          </div>
          {byDept.get(dept).map((c) => {
            const isEditing = draft?.id === c.id;
            if (isEditing) {
              return (
                <ContactRowForm
                  key={c.id}
                  draft={draft}
                  setDraft={setDraft}
                  onSave={save}
                  onCancel={cancel}
                  busy={busy}
                />
              );
            }
            return (
              <div
                key={c.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                  fontSize: "13px",
                  padding: "4px 0",
                }}
              >
                <strong>{c.name || "(unnamed)"}</strong>
                {c.phone && <span>· {c.phone}</span>}
                {c.email && <span>· {c.email}</span>}
                <div style={{ marginLeft: "auto", display: "flex", gap: "6px" }}>
                  <button
                    style={{ ...styles.button, margin: 0 }}
                    onClick={() => startEdit(c)}
                    disabled={busy || !!draft}
                  >
                    Edit
                  </button>
                  <button
                    style={{
                      ...styles.button,
                      margin: 0,
                      background: "#fee",
                      color: "#a00",
                    }}
                    onClick={() => remove(c)}
                    disabled={busy || !!draft}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {draft && !draft.id && (
        <ContactRowForm
          draft={draft}
          setDraft={setDraft}
          onSave={save}
          onCancel={cancel}
          busy={busy}
        />
      )}

      {!draft && (
        <button
          style={{ ...styles.button, marginTop: "4px" }}
          onClick={startAdd}
        >
          + Add Contact
        </button>
      )}
    </div>
  );
}

function ContactRowForm({ draft, setDraft, onSave, onCancel, busy }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        gap: "8px",
        margin: "6px 0",
        padding: "8px",
        background: "#fafafa",
        border: "1px solid #eee",
        borderRadius: "4px",
      }}
    >
      <input
        style={{ ...styles.input, margin: 0 }}
        type="text"
        placeholder="Name (required)"
        value={draft.name}
        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
      />
      <input
        style={{ ...styles.input, margin: 0 }}
        type="text"
        placeholder="Department"
        value={draft.department}
        onChange={(e) => setDraft({ ...draft, department: e.target.value })}
      />
      <input
        style={{ ...styles.input, margin: 0 }}
        type="tel"
        placeholder="Phone"
        value={draft.phone}
        onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
      />
      <input
        style={{ ...styles.input, margin: 0 }}
        type="email"
        placeholder="Email"
        value={draft.email}
        onChange={(e) => setDraft({ ...draft, email: e.target.value })}
      />
      <div style={{ display: "flex", gap: "6px", gridColumn: "1 / -1" }}>
        <button
          style={{ ...styles.primaryButton, margin: 0 }}
          onClick={onSave}
          disabled={busy}
        >
          {busy ? "Saving..." : "Save Contact"}
        </button>
        <button
          style={{ ...styles.button, margin: 0 }}
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
